import React from 'react';
import {
  CircleNotch,
  FloppyDisk,
  Pencil,
  Trash,
  X,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TableCell, TableRow } from '../../components/ui/table';
import { formatCurrency } from '../../lib/utils';

const RateValueCell = ({ value, isEditing, fieldKey, editingRate, setEditingRate, onStartEdit, mono = false, color = 'emerald' }) => {
  if (isEditing) {
    return (
      <Input
        type="number"
        step="0.01"
        value={editingRate[fieldKey]}
        onChange={(e) =>
          setEditingRate((prev) => ({ ...prev, [fieldKey]: parseFloat(e.target.value) || 0 }))
        }
        className="w-24 h-8 text-sm font-mono"
        data-testid={`edit-${fieldKey.replace('_', '-')}-${editingRate.hcpcs_code}`}
      />
    );
  }
  return (
    <span
      className={`font-mono ${mono ? 'font-semibold' : ''} text-${color}-600 cursor-pointer hover:bg-${color}-50 px-2 py-1 rounded`}
      onClick={onStartEdit}
      title="Click to edit"
    >
      {formatCurrency(value)}
    </span>
  );
};

/**
 * Single billing-rate row inside a contract's rates table.
 * Switches between read-only and inline-edit mode based on `editingRate`.
 */
const RateRow = ({
  rate, contractId, editingRate, setEditingRate,
  savingRate, onStartEdit, onSaveRate, onDeleteRate,
}) => {
  const isEditing =
    editingRate?.contractId === contractId && editingRate?.hcpcs_code === rate.hcpcs_code;

  return (
    <TableRow className={isEditing ? 'bg-indigo-50' : ''}>
      <TableCell>
        <span className="font-mono font-semibold text-indigo-700">{rate.hcpcs_code}</span>
      </TableCell>
      <TableCell className="text-sm text-slate-700">{rate.description}</TableCell>
      <TableCell><span className="text-xs text-slate-500">{rate.unit_type}</span></TableCell>
      <TableCell>
        <RateValueCell
          value={rate.unit_rate}
          isEditing={isEditing}
          fieldKey="unit_rate"
          editingRate={editingRate}
          setEditingRate={setEditingRate}
          onStartEdit={() => onStartEdit(contractId, rate)}
          mono
          color="emerald"
        />
      </TableCell>
      <TableCell>
        <RateValueCell
          value={rate.base_rate || 0}
          isEditing={isEditing}
          fieldKey="base_rate"
          editingRate={editingRate}
          setEditingRate={setEditingRate}
          onStartEdit={() => onStartEdit(contractId, rate)}
          color="slate"
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {(rate.modifiers || []).map((mod) => (
            <Badge key={mod} variant="secondary" className="text-xs font-mono px-1.5">{mod}</Badge>
          ))}
          {(!rate.modifiers || rate.modifiers.length === 0) && (
            <span className="text-xs text-slate-400">—</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex gap-1">
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-emerald-600"
              onClick={onSaveRate}
              disabled={savingRate}
              data-testid={`save-rate-${rate.hcpcs_code}`}
            >
              {savingRate ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />}
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-slate-400"
              onClick={() => setEditingRate(null)}
            >
              <X size={14} />
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600"
              onClick={() => onStartEdit(contractId, rate)}
              data-testid={`edit-rate-btn-${rate.hcpcs_code}`}
            >
              <Pencil size={14} />
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
              onClick={() => onDeleteRate(contractId, rate.hcpcs_code)}
            >
              <Trash size={14} />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default RateRow;
